import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/base/Breadcrumb';
import WorkCard from '@/components/feature/WorkCard';
import { works } from '@/mocks/works';
import { usePageSeo } from '@/hooks/usePageSeo';

const PAGE_SIZE = 12;

type TypeFilter = '全部' | '电影' | '电视剧';
type SortKey = 'time' | 'popular';

export default function WorksCategory() {
  usePageSeo({
    title: '小仓优子影视作品介绍_电影电视剧作品列表',
    description:
      '小仓优子影视作品全集，涵盖电影与电视剧代表作，支持按类型、年份筛选及按时间、热度排序，快速查阅每部作品简介与角色信息。',
    keywords: '小仓优子作品,影视作品列表,小仓优子电影,小仓优子电视剧,演员作品',
  });

  const [type, setType] = useState<TypeFilter>('全部');
  const [year, setYear] = useState<string>('全部');
  const [sort, setSort] = useState<SortKey>('time');
  const [page, setPage] = useState(1);

  const years = useMemo(() => {
    const set = new Set(works.map((w) => String(w.year)));
    return ['全部', ...Array.from(set).sort((a, b) => Number(b) - Number(a))];
  }, []);

  const filtered = useMemo(() => {
    const list = works.filter((w) => {
      const typeOk = type === '全部' || w.type === type;
      const yearOk = year === '全部' || String(w.year) === year;
      return typeOk && yearOk;
    });
    return list.sort((a, b) => {
      if (sort === 'popular') return b.popularity - a.popularity;
      return b.year - a.year;
    });
  }, [type, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleReset = () => {
    setType('全部');
    setYear('全部');
    setSort('time');
    setPage(1);
  };

  const changeType = (t: TypeFilter) => {
    setType(t);
    setPage(1);
  };
  const changeYear = (y: string) => {
    setYear(y);
    setPage(1);
  };
  const changeSort = (s: SortKey) => {
    setSort(s);
    setPage(1);
  };

  const typeOptions: TypeFilter[] = ['全部', '电影', '电视剧'];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full bg-gradient-to-br from-primary-50 via-background-50 to-secondary-50 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视作品' }]} />
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-5">小仓优子影视作品介绍</h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4 max-w-3xl">
              这里收录了小仓优子自出道以来参演的电影与电视剧作品。从青春校园到都市悬疑，再到文艺家庭题材，她不断尝试不同类型的角色，用细腻的表演为每一个故事注入温度。你可以按类型、年份筛选，或按时间与热度排序，快速找到想了解的作品。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="p-4 md:p-5 rounded-lg bg-background-100 border border-background-200 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-1">类型</span>
                <div className="flex items-center bg-background-50 border border-background-200 rounded-full px-1 py-1">
                  {typeOptions.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => changeType(t)}
                      className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        type === t ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">年份</span>
                  <select
                    value={year}
                    onChange={(e) => changeYear(e.target.value)}
                    className="text-sm bg-background-50 border border-background-200 rounded-md px-3 py-2 text-foreground-800 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-400"
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y === '全部' ? '全部年份' : `${y} 年`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">排序</span>
                  <select
                    value={sort}
                    onChange={(e) => changeSort(e.target.value as SortKey)}
                    className="text-sm bg-background-50 border border-background-200 rounded-md px-3 py-2 text-foreground-800 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-400"
                  >
                    <option value="time">按时间（最新）</option>
                    <option value="popular">按热度（最热）</option>
                  </select>
                </div>
              </div>

              <div className="lg:ml-auto text-sm text-foreground-500">
                共 <span className="text-accent-600 font-medium">{filtered.length}</span> 部作品
              </div>
            </div>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {pageItems.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-4 py-2 rounded-md text-sm border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setPage(n)}
                      className={`w-10 h-10 rounded-md text-sm transition-colors cursor-pointer ${
                        n === currentPage
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="px-4 py-2 rounded-md text-sm border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="py-20 text-center">
              <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-300">
                <i className="ri-inbox-line text-3xl"></i>
              </span>
              <p className="text-foreground-600 mt-5">暂无相关影视作品</p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-5 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选条件
              </button>
            </div>
          )}

          <div className="mt-12 flex justify-center">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}